import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import DashboardLayout from "@/components/DashboardLayout";
import { api, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Loader2, ArrowLeft, HandCoins, AlertCircle, Save, Send } from "lucide-react";

/**
 * Funding-request submission form — الجهة الممولة.
 *
 * Single-page RTL form. Same component is used for both:
 *   • /dashboard/funding/requests/new
 *   • /dashboard/funding/requests/:id/edit
 *
 * Wave 3B rules:
 *   - On first save, navigate to the /edit route to persist the identity across reloads.
 *   - When beneficiary_type or geographic_scope changes, hide AND clear the sibling
 *     fields that are no longer relevant (mirrors Wave 3A backend clearing).
 *   - Show validation errors beside each field.
 *   - Prevent double-click during save/submit.
 *   - Idempotency-Key is auto-attached by the axios interceptor (POST + PATCH funding routes).
 */

const CONTRIBUTION_LABELS = {
  FINANCIAL: "مساهمة مالية",
  IN_KIND: "مساهمة عينية",
  SPONSORSHIP: "رعاية",
  OTHER: "أخرى",
};

const BENEFICIARY_LABELS = {
  STUDENTS: "طلاب",
  ASSOCIATIONS: "جمعيات",
  BOTH: "طلاب وجمعيات",
};

const SCOPE_LABELS = {
  KINGDOM_WIDE: "على مستوى المملكة",
  REGION: "منطقة محددة",
  CITY: "مدينة محددة",
};

const DURATION_UNIT_LABELS = {
  WEEKS: "أسابيع",
  MONTHS: "أشهر",
};

const EMPTY_FORM = {
  funding_goal: "",
  contribution_type: "",
  estimated_amount_or_value: "",
  beneficiary_type: "",
  students_count: "",
  associations_count: "",
  geographic_scope: "",
  region: "",
  city: "",
  duration_value: "",
  duration_unit: "MONTHS",
  request_details: "",
};

function Field({ id, label, required, error, children, hint }) {
  return (
    <div className="space-y-1.5" data-testid={`field-${id}`}>
      <Label htmlFor={id} className="text-sm font-semibold text-slate-800">
        {label} {required && <span className="text-rose-600">*</span>}
      </Label>
      {children}
      {hint && !error && (
        <p className="text-xs text-slate-500 mt-1" data-testid={`hint-${id}`}>{hint}</p>
      )}
      {error && (
        <p className="text-xs text-rose-600 mt-1 flex items-center gap-1" data-testid={`error-${id}`}>
          <AlertCircle className="h-3 w-3" />
          <span>{error}</span>
        </p>
      )}
    </div>
  );
}

function ChoiceGroup({ id, value, onChange, options, disabled }) {
  return (
    <div className="flex flex-wrap gap-2" role="radiogroup" data-testid={`choicegroup-${id}`}>
      {Object.entries(options).map(([val, label]) => {
        const active = value === val;
        return (
          <button
            key={val}
            type="button"
            role="radio"
            aria-checked={active}
            disabled={disabled}
            onClick={() => onChange(val)}
            data-testid={`${id}-${val}`}
            className={`px-4 py-2 rounded-full border text-sm font-medium transition-colors ${
              active
                ? "bg-teal-700 border-teal-700 text-white"
                : "bg-white border-slate-300 text-slate-700 hover:border-teal-500"
            } disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            {label}
          </button>
        );
      })}
    </div>
  );
}

function buildFormFromRow(row) {
  const dv = row.target_execution_period?.duration_value ?? "";
  const du = row.target_execution_period?.duration_unit ?? "MONTHS";
  return {
    funding_goal: row.funding_goal ?? "",
    contribution_type: row.contribution_type ?? "",
    estimated_amount_or_value: row.estimated_amount_or_value ?? "",
    beneficiary_type: row.beneficiary_type ?? "",
    students_count: row.students_count ?? "",
    associations_count: row.associations_count ?? "",
    geographic_scope: row.geographic_scope ?? "",
    region: row.region ?? "",
    city: row.city ?? "",
    duration_value: dv === null ? "" : String(dv),
    duration_unit: du || "MONTHS",
    request_details: row.request_details ?? "",
  };
}

function buildPayload(form) {
  // Only include fields the client is authorised to set. Server rejects everything else.
  const p = {};
  if (form.funding_goal?.trim()) p.funding_goal = form.funding_goal.trim();
  if (form.contribution_type) p.contribution_type = form.contribution_type;
  if (form.estimated_amount_or_value !== "" && form.estimated_amount_or_value != null) {
    p.estimated_amount_or_value = String(form.estimated_amount_or_value).trim();
  }
  if (form.beneficiary_type) p.beneficiary_type = form.beneficiary_type;
  if (form.beneficiary_type === "STUDENTS" || form.beneficiary_type === "BOTH") {
    if (form.students_count !== "" && form.students_count != null)
      p.students_count = Number(form.students_count);
  }
  if (form.beneficiary_type === "ASSOCIATIONS" || form.beneficiary_type === "BOTH") {
    if (form.associations_count !== "" && form.associations_count != null)
      p.associations_count = Number(form.associations_count);
  }
  if (form.geographic_scope) p.geographic_scope = form.geographic_scope;
  if (form.geographic_scope === "REGION" || form.geographic_scope === "CITY") {
    if (form.region?.trim()) p.region = form.region.trim();
  }
  if (form.geographic_scope === "CITY") {
    if (form.city?.trim()) p.city = form.city.trim();
  }
  if (form.duration_value !== "" && form.duration_value != null) {
    p.target_execution_period = {
      duration_value: Number(form.duration_value),
      duration_unit: form.duration_unit || "MONTHS",
    };
  }
  if (form.request_details?.trim()) p.request_details = form.request_details.trim();
  return p;
}

function validateForSubmit(form) {
  const errs = {};
  if (!form.funding_goal?.trim()) errs.funding_goal = "حقل مطلوب";
  else if (form.funding_goal.trim().length > 500) errs.funding_goal = "الحد الأقصى 500 حرف";
  if (!form.contribution_type) errs.contribution_type = "حقل مطلوب";
  if (!form.estimated_amount_or_value) errs.estimated_amount_or_value = "حقل مطلوب";
  else if (!/^\d+(\.\d{1,2})?$/.test(String(form.estimated_amount_or_value).trim()))
    errs.estimated_amount_or_value = "صيغة الرقم غير صحيحة (مثال: 50000 أو 50000.00)";
  if (!form.beneficiary_type) errs.beneficiary_type = "حقل مطلوب";
  else {
    if (form.beneficiary_type === "STUDENTS" || form.beneficiary_type === "BOTH") {
      const n = Number(form.students_count);
      if (!form.students_count || !Number.isInteger(n) || n < 1)
        errs.students_count = "عدد صحيح ≥ 1";
    }
    if (form.beneficiary_type === "ASSOCIATIONS" || form.beneficiary_type === "BOTH") {
      const n = Number(form.associations_count);
      if (!form.associations_count || !Number.isInteger(n) || n < 1)
        errs.associations_count = "عدد صحيح ≥ 1";
    }
  }
  if (!form.geographic_scope) errs.geographic_scope = "حقل مطلوب";
  else {
    if (form.geographic_scope === "REGION" && !form.region?.trim())
      errs.region = "حقل مطلوب";
    if (form.geographic_scope === "CITY") {
      if (!form.region?.trim()) errs.region = "حقل مطلوب";
      if (!form.city?.trim()) errs.city = "حقل مطلوب";
    }
  }
  const dv = Number(form.duration_value);
  if (!form.duration_value || !Number.isInteger(dv) || dv <= 0 || dv > 500)
    errs.duration_value = "عدد صحيح موجب ≤ 500";
  if (!form.request_details?.trim()) errs.request_details = "حقل مطلوب";
  else if (form.request_details.trim().length > 4000)
    errs.request_details = "الحد الأقصى 4000 حرف";
  return errs;
}

function serverErrorsToFieldMap(err) {
  const detail = err?.response?.data?.detail;
  const out = {};
  if (Array.isArray(detail?.errors)) {
    for (const e of detail.errors) {
      const f = (e.field || "").split(".")[0];
      const k = f === "target_execution_period" ? "duration_value" : f;
      if (k) out[k] = e.message || e.code || "قيمة غير مقبولة";
    }
  }
  return out;
}

export default function FundingRequestForm() {
  const { id: routeId } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(routeId);

  const [requestId, setRequestId] = useState(routeId || null);
  const [lockVersion, setLockVersion] = useState(null);
  const [status, setStatus] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(isEdit);
  const [loadError, setLoadError] = useState(null);
  const [busy, setBusy] = useState(false); // any save/submit in flight → disables both buttons
  const [lastBusyKind, setLastBusyKind] = useState(null); // 'draft' | 'submit'

  // ---- Load existing draft if editing --------------------------------
  useEffect(() => {
    let cancel = false;
    if (!isEdit) return;
    (async () => {
      setLoading(true);
      setLoadError(null);
      try {
        const { data } = await api.get(`/funding/requests/${routeId}`);
        if (cancel) return;
        const row = data.request;
        setForm(buildFormFromRow(row));
        setLockVersion(row.lock_version);
        setStatus(row.status);
        setRequestId(row.id);
      } catch (e) {
        if (!cancel) setLoadError(formatApiError(e));
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => { cancel = true; };
  }, [isEdit, routeId]);

  const readOnly = status && status !== "DRAFT";

  // ---- Field change with conditional clearing ------------------------
  const setField = (name, value) => {
    setForm((prev) => {
      const next = { ...prev, [name]: value };
      if (name === "beneficiary_type") {
        if (value === "STUDENTS") next.associations_count = "";
        else if (value === "ASSOCIATIONS") next.students_count = "";
      }
      if (name === "geographic_scope") {
        if (value === "KINGDOM_WIDE") { next.region = ""; next.city = ""; }
        else if (value === "REGION") { next.city = ""; }
      }
      return next;
    });
    setErrors((prev) => {
      if (!prev || !Object.keys(prev).length) return prev;
      const c = { ...prev };
      delete c[name];
      if (name === "beneficiary_type") { delete c.students_count; delete c.associations_count; }
      if (name === "geographic_scope") { delete c.region; delete c.city; }
      return c;
    });
  };

  const showStudentsCount = form.beneficiary_type === "STUDENTS" || form.beneficiary_type === "BOTH";
  const showAssociationsCount = form.beneficiary_type === "ASSOCIATIONS" || form.beneficiary_type === "BOTH";
  const showRegion = form.geographic_scope === "REGION" || form.geographic_scope === "CITY";
  const showCity = form.geographic_scope === "CITY";

  // ---- Save actions --------------------------------------------------
  async function persist({ submitAfterSave }) {
    if (busy) return;
    // For "submit", validate fully; for "save draft", only surface field-shape errors reactively.
    if (submitAfterSave) {
      const v = validateForSubmit(form);
      if (Object.keys(v).length) {
        setErrors(v);
        toast.error("يرجى تصحيح الحقول المطلوبة قبل الإرسال");
        return;
      }
    }
    setErrors({});
    setBusy(true);
    setLastBusyKind(submitAfterSave ? "submit" : "draft");
    try {
      const payload = buildPayload(form);
      let currentId = requestId;
      let currentLock = lockVersion;
      if (!currentId) {
        const { data } = await api.post(`/funding/requests`, payload);
        currentId = data.id;
        currentLock = data.lock_version;
        setRequestId(currentId);
        setLockVersion(currentLock);
        setStatus(data.status);
        // Navigate to /edit so identity is retained on reload
        navigate(`/dashboard/funding/requests/${currentId}/edit`, { replace: true });
      } else {
        const { data } = await api.patch(`/funding/requests/${currentId}`, {
          ...payload,
          _expected_lock_version: currentLock,
        });
        currentLock = data.lock_version;
        setLockVersion(currentLock);
        setStatus(data.status);
      }
      if (submitAfterSave) {
        const { data } = await api.post(`/funding/requests/${currentId}/submit`, {
          _expected_lock_version: currentLock,
        });
        setStatus(data.status);
        setLockVersion(data.lock_version);
        toast.success("تم إرسال الطلب بنجاح");
      } else {
        toast.success("تم حفظ المسودة");
      }
    } catch (e) {
      const fieldMap = serverErrorsToFieldMap(e);
      if (Object.keys(fieldMap).length) setErrors(fieldMap);
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
      setLastBusyKind(null);
    }
  }

  // ---- Render --------------------------------------------------------
  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center py-24" data-testid="funding-form-loading">
          <Loader2 className="h-8 w-8 animate-spin text-teal-700" />
        </div>
      </DashboardLayout>
    );
  }

  if (loadError) {
    return (
      <DashboardLayout>
        <div className="p-5 rounded-2xl bg-rose-50 border border-rose-200" data-testid="funding-form-load-error">
          <div className="flex items-start gap-3">
            <AlertCircle className="h-6 w-6 text-rose-700 shrink-0 mt-1" />
            <div>
              <h3 className="font-heading font-bold text-rose-900">تعذّر تحميل المسودة</h3>
              <p className="text-sm text-rose-800 mt-1">{loadError}</p>
              <Button
                variant="outline"
                className="mt-3"
                onClick={() => navigate("/dashboard")}
                data-testid="funding-form-back-to-dashboard"
              >
                العودة للوحة
              </Button>
            </div>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-3xl mx-auto" data-testid="funding-form-page">
        {/* Header */}
        <div className="mb-6">
          <button
            type="button"
            onClick={() => navigate("/dashboard")}
            className="text-xs text-slate-500 hover:text-slate-800 inline-flex items-center gap-1 mb-3"
            data-testid="funding-form-back-btn"
          >
            <ArrowLeft className="h-3 w-3" /> العودة
          </button>
          <div className="flex items-start gap-3">
            <div className="h-11 w-11 rounded-xl bg-amber-50 text-amber-700 grid place-items-center shrink-0">
              <HandCoins className="h-5 w-5" strokeWidth={1.75} />
            </div>
            <div className="flex-1 min-w-0">
              <h1 className="text-2xl font-heading font-extrabold text-slate-900">
                {isEdit ? "تعديل طلب التمويل" : "تقديم طلب تمويل"}
              </h1>
              <p className="text-sm text-slate-600 mt-1">
                عبّئ الحقول التالية بدقة. يمكنك الحفظ كمسودة والعودة لاحقاً قبل الإرسال.
                {status && status !== "DRAFT" && (
                  <span className="ms-2 inline-flex items-center gap-1 text-teal-700 font-semibold">
                    · الحالة: <span data-testid="funding-form-status">{status}</span>
                  </span>
                )}
              </p>
            </div>
          </div>
        </div>

        {readOnly && (
          <div className="mb-6 p-4 rounded-xl bg-amber-50 border border-amber-200 text-sm text-amber-900" data-testid="funding-form-readonly-banner">
            هذا الطلب لم يعد قابلاً للتعديل لأن حالته لم تعد مسودة.
          </div>
        )}

        <form
          className="space-y-6 rounded-2xl bg-white border border-cream-200 card-shadow p-5"
          onSubmit={(e) => { e.preventDefault(); persist({ submitAfterSave: true }); }}
          data-testid="funding-form"
        >
          {/* Funding goal */}
          <Field id="funding_goal" label="هدف التمويل" required error={errors.funding_goal}
                 hint="لخّص الغرض من التمويل بجملة أو جملتين. الحد الأقصى 500 حرف.">
            <Input
              id="funding_goal"
              value={form.funding_goal}
              onChange={(e) => setField("funding_goal", e.target.value)}
              maxLength={500}
              disabled={readOnly}
              placeholder="مثال: دعم تدريب 100 طالب على مهارات سوق العمل"
              data-testid="input-funding_goal"
            />
          </Field>

          {/* Contribution type */}
          <Field id="contribution_type" label="نوع المساهمة" required error={errors.contribution_type}>
            <ChoiceGroup
              id="contribution_type"
              value={form.contribution_type}
              onChange={(v) => setField("contribution_type", v)}
              options={CONTRIBUTION_LABELS}
              disabled={readOnly}
            />
          </Field>

          {/* Amount */}
          <Field id="estimated_amount_or_value" label="المبلغ أو القيمة التقديرية (ريال سعودي)"
                 required error={errors.estimated_amount_or_value}
                 hint="أدخل رقماً موجباً بدون فواصل. يمكن استخدام خانتين عشريتين.">
            <Input
              id="estimated_amount_or_value"
              inputMode="decimal"
              value={form.estimated_amount_or_value}
              onChange={(e) => setField("estimated_amount_or_value", e.target.value)}
              disabled={readOnly}
              placeholder="50000"
              data-testid="input-estimated_amount"
            />
          </Field>

          {/* Beneficiary type */}
          <Field id="beneficiary_type" label="نوع المستفيد" required error={errors.beneficiary_type}>
            <ChoiceGroup
              id="beneficiary_type"
              value={form.beneficiary_type}
              onChange={(v) => setField("beneficiary_type", v)}
              options={BENEFICIARY_LABELS}
              disabled={readOnly}
            />
          </Field>

          {/* Conditional counts */}
          {(showStudentsCount || showAssociationsCount) && (
            <div className="grid gap-4 sm:grid-cols-2" data-testid="beneficiary-counts">
              {showStudentsCount && (
                <Field id="students_count" label="عدد الطلاب المستفيدين" required error={errors.students_count}>
                  <Input
                    id="students_count"
                    type="number"
                    inputMode="numeric"
                    min={1}
                    value={form.students_count}
                    onChange={(e) => setField("students_count", e.target.value)}
                    disabled={readOnly}
                    data-testid="input-students_count"
                  />
                </Field>
              )}
              {showAssociationsCount && (
                <Field id="associations_count" label="عدد الجمعيات المستفيدة" required error={errors.associations_count}>
                  <Input
                    id="associations_count"
                    type="number"
                    inputMode="numeric"
                    min={1}
                    value={form.associations_count}
                    onChange={(e) => setField("associations_count", e.target.value)}
                    disabled={readOnly}
                    data-testid="input-associations_count"
                  />
                </Field>
              )}
            </div>
          )}

          {/* Geographic scope */}
          <Field id="geographic_scope" label="النطاق الجغرافي" required error={errors.geographic_scope}>
            <ChoiceGroup
              id="geographic_scope"
              value={form.geographic_scope}
              onChange={(v) => setField("geographic_scope", v)}
              options={SCOPE_LABELS}
              disabled={readOnly}
            />
          </Field>

          {/* Conditional region/city */}
          {(showRegion || showCity) && (
            <div className="grid gap-4 sm:grid-cols-2" data-testid="geo-fields">
              {showRegion && (
                <Field id="region" label="المنطقة" required error={errors.region}>
                  <Input
                    id="region"
                    value={form.region}
                    onChange={(e) => setField("region", e.target.value)}
                    maxLength={120}
                    disabled={readOnly}
                    placeholder="مثال: منطقة الرياض"
                    data-testid="input-region"
                  />
                </Field>
              )}
              {showCity && (
                <Field id="city" label="المدينة" required error={errors.city}>
                  <Input
                    id="city"
                    value={form.city}
                    onChange={(e) => setField("city", e.target.value)}
                    maxLength={120}
                    disabled={readOnly}
                    placeholder="مثال: الرياض"
                    data-testid="input-city"
                  />
                </Field>
              )}
            </div>
          )}

          {/* Duration */}
          <Field id="duration_value" label="مدة التنفيذ" required error={errors.duration_value}>
            <div className="flex gap-2">
              <Input
                id="duration_value"
                type="number"
                inputMode="numeric"
                min={1}
                max={500}
                value={form.duration_value}
                onChange={(e) => setField("duration_value", e.target.value)}
                disabled={readOnly}
                className="max-w-[160px]"
                placeholder="مثال: 6"
                data-testid="input-duration_value"
              />
              <ChoiceGroup
                id="duration_unit"
                value={form.duration_unit}
                onChange={(v) => setField("duration_unit", v)}
                options={DURATION_UNIT_LABELS}
                disabled={readOnly}
              />
            </div>
          </Field>

          {/* Details */}
          <Field id="request_details" label="تفاصيل الطلب" required error={errors.request_details}
                 hint="اشرح خلفية الطلب، والفئة المستهدفة، والنتائج المتوقعة. الحد الأقصى 4000 حرف.">
            <Textarea
              id="request_details"
              rows={6}
              value={form.request_details}
              onChange={(e) => setField("request_details", e.target.value)}
              maxLength={4000}
              disabled={readOnly}
              placeholder="اكتب هنا…"
              data-testid="input-request_details"
            />
          </Field>

          {/* Actions */}
          <div className="flex flex-col-reverse sm:flex-row items-stretch sm:items-center justify-end gap-3 pt-2 border-t border-slate-100">
            <Button
              type="button"
              variant="outline"
              disabled={busy || readOnly}
              onClick={() => persist({ submitAfterSave: false })}
              data-testid="btn-save-draft"
              className="min-w-[160px]"
            >
              {busy && lastBusyKind === "draft" ? (
                <><Loader2 className="h-4 w-4 animate-spin ms-2" /> جاري الحفظ…</>
              ) : (
                <><Save className="h-4 w-4 ms-2" /> حفظ كمسودة</>
              )}
            </Button>
            <Button
              type="submit"
              disabled={busy || readOnly}
              className="min-w-[160px] bg-teal-700 hover:bg-teal-800"
              data-testid="btn-submit-request"
            >
              {busy && lastBusyKind === "submit" ? (
                <><Loader2 className="h-4 w-4 animate-spin ms-2" /> جاري الإرسال…</>
              ) : (
                <><Send className="h-4 w-4 ms-2" /> إرسال الطلب</>
              )}
            </Button>
          </div>
        </form>
      </div>
    </DashboardLayout>
  );
}
